import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canUsePrebid } from './utils/access';
import Sidebar from './components/Sidebar';
import ImpersonationBanner from './components/ImpersonationBanner';
import Login from './pages/Login';
import PublicSign from './pages/PublicSign';
import Dashboard from './pages/Dashboard';
import SendContract from './pages/SendContract';
import AddRecord from './pages/AddRecord';
import UpdateRecord from './pages/UpdateRecord';
import JobTracker from './pages/JobTracker';
import SalesReport from './pages/SalesReport';
import Contractors from './pages/Contractors';
import Staff from './pages/Staff';
import MyProfile from './pages/MyProfile';
import Appointments from './pages/Appointments';
import BookAppointment from './pages/BookAppointment';
import Dispatch from './pages/Dispatch';
import Visit from './pages/Visit';
import FormsList from './pages/FormsList';
import StaffReport from './pages/StaffReport';
import Collection from './pages/Collection';
import AttachmentsPortal from './pages/AttachmentsPortal';
import Users from './pages/Users';
import DataSafety from './pages/DataSafety';
import Training from './pages/Training';
import TrainingLesson from './pages/TrainingLesson';
import TrainingQuiz from './pages/TrainingQuiz';
import TakeTest from './pages/TakeTest';
import TrainingAdmin from './pages/TrainingAdmin';
import QuickBooks from './pages/QuickBooks';
import Presentations from './pages/Presentations';
import IDCards from './pages/IDCards';
import CalendarPage from './pages/CalendarPage';
import MyJobs from './pages/MyJobs';
import MyDashboard from './pages/MyDashboard';
import CeoDashboard from './pages/CeoDashboard';
import CollectionReport from './pages/CollectionReport';
import ContractorPortal from './pages/ContractorPortal';
import ChangeOrders from './pages/ChangeOrders';
import ContractorPackets from './pages/ContractorPackets';
import CanvasserPayroll from './pages/CanvasserPayroll';
import PaintingCalculatorPage from './pages/PaintingCalculatorPage';
import CompletedProjects from './pages/CompletedProjects';
import ObjectionRoom from './pages/ObjectionRoom';
import Testimonials from './pages/Testimonials';
import InvestmentReview from './pages/InvestmentReview';
import PerformanceBoard from './pages/PerformanceBoard';
import PaymentSheet from './pages/PaymentSheet';
import PreBidCalculator from './pages/PreBidCalculator';
import ManualPrebid from './pages/ManualPrebid';
import RoofingPrebid from './pages/RoofingPrebid';
import CoverSheet from './pages/CoverSheet';
import OnSiteReport from './pages/OnSiteReport';
import PLSheet from './pages/PLSheet';

function ProtectedLayout({ children }) {
  const { isAuthenticated, isAttachments, isStaff, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isAttachments) return <Navigate to="/attachments-portal" replace />;
  if (isStaff) return <Navigate to="/my-dashboard" replace />;
  if (isContractor) return <Navigate to="/contractor-portal" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function StaffLayout({ children }) {
  const { isAuthenticated, isStaff } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

function ContractorLayout({ children }) {
  const { isAuthenticated, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isContractor) return <Navigate to="/" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

// Joe's home page is the CEO dashboard; everyone else keeps the normal one
const CEO_LOGINS = ['joe'];
function HomeDashboard() {
  const { username } = useAuth();
  return CEO_LOGINS.includes(String(username || '').toLowerCase()) ? <CeoDashboard /> : <Dashboard />;
}

function SharedLayout({ children }) {
  // Accessible to any authenticated, non-portal user (staff reps AND admins)
  const { isAuthenticated, isAttachments, isContractor } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isAttachments) return <Navigate to="/attachments-portal" replace />;
  if (isContractor) return <Navigate to="/contractor-portal" replace />;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content"><ImpersonationBanner />{children}</div>
    </div>
  );
}

// Pre-Bid Calculator: admins + the people listed in utils/access.js
function PrebidOnly({ children }) {
  const auth = useAuth();
  if (!canUsePrebid(auth)) return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sign/:token" element={<PublicSign />} />
          <Route path="/visit/:token" element={<Visit />} />
          <Route path="/attachments-portal" element={<AttachmentsPortal />} />
          <Route path="/my-jobs" element={<StaffLayout><MyJobs /></StaffLayout>} />
          <Route path="/my-dashboard" element={<SharedLayout><MyDashboard /></SharedLayout>} />
          <Route path="/contractor-portal" element={<ContractorLayout><ContractorPortal /></ContractorLayout>} />
          <Route path="/" element={<ProtectedLayout><HomeDashboard /></ProtectedLayout>} />
          <Route path="/tracker" element={<ProtectedLayout><JobTracker /></ProtectedLayout>} />
          <Route path="/send-contract" element={<SharedLayout><SendContract /></SharedLayout>} />
          <Route path="/staff-report" element={<ProtectedLayout><StaffReport /></ProtectedLayout>} />
          <Route path="/sales" element={<ProtectedLayout><SalesReport /></ProtectedLayout>} />
          <Route path="/collection" element={<ProtectedLayout><Collection /></ProtectedLayout>} />
          {/* Money In now lives inside Collection → "Collected" tab; old links still land there */}
          <Route path="/money-in" element={<Navigate to="/collection?tab=collected" replace />} />
          <Route path="/cashflow" element={<ProtectedLayout><AdminOnly><PaymentSheet /></AdminOnly></ProtectedLayout>} />
          <Route path="/collection-report" element={<ProtectedLayout><AdminOnly><CollectionReport /></AdminOnly></ProtectedLayout>} />
          <Route path="/pre-bid" element={<SharedLayout><PrebidOnly><PreBidCalculator /></PrebidOnly></SharedLayout>} />
          <Route path="/pre-bid-manual" element={<SharedLayout><PrebidOnly><ManualPrebid /></PrebidOnly></SharedLayout>} />
          <Route path="/pre-bid-roofing" element={<SharedLayout><PrebidOnly><RoofingPrebid /></PrebidOnly></SharedLayout>} />
          <Route path="/calendar" element={<ProtectedLayout><CalendarPage /></ProtectedLayout>} />
          <Route path="/change-orders" element={<ProtectedLayout><ChangeOrders /></ProtectedLayout>} />
          <Route path="/contractor-packets" element={<ProtectedLayout><AdminOnly><ContractorPackets /></AdminOnly></ProtectedLayout>} />
          <Route path="/canvasser-payroll" element={<ProtectedLayout><AdminOnly><CanvasserPayroll /></AdminOnly></ProtectedLayout>} />
          <Route path="/painting-calculator" element={<SharedLayout><PaintingCalculatorPage /></SharedLayout>} />
          <Route path="/projects" element={<SharedLayout><CompletedProjects /></SharedLayout>} />
          <Route path="/objections" element={<SharedLayout><ObjectionRoom /></SharedLayout>} />
          <Route path="/testimonials" element={<SharedLayout><Testimonials /></SharedLayout>} />
          <Route path="/investment-review" element={<SharedLayout><InvestmentReview /></SharedLayout>} />
          <Route path="/board" element={<SharedLayout><PerformanceBoard /></SharedLayout>} />
          <Route path="/add" element={<ProtectedLayout><AdminOnly><AddRecord /></AdminOnly></ProtectedLayout>} />
          <Route path="/update" element={<ProtectedLayout><AdminOnly><UpdateRecord /></AdminOnly></ProtectedLayout>} />
          <Route path="/cover-sheet" element={<ProtectedLayout><AdminOnly><CoverSheet /></AdminOnly></ProtectedLayout>} />
          <Route path="/on-site-report" element={<ProtectedLayout><AdminOnly><OnSiteReport /></AdminOnly></ProtectedLayout>} />
          <Route path="/pl-sheet" element={<ProtectedLayout><AdminOnly><PLSheet /></AdminOnly></ProtectedLayout>} />
          <Route path="/contractors" element={<ProtectedLayout><AdminOnly><Contractors /></AdminOnly></ProtectedLayout>} />
          <Route path="/staff" element={<ProtectedLayout><AdminOnly><Staff /></AdminOnly></ProtectedLayout>} />
          <Route path="/my-profile" element={<SharedLayout><MyProfile /></SharedLayout>} />
          <Route path="/appointments" element={<ProtectedLayout><AdminOnly><Appointments /></AdminOnly></ProtectedLayout>} />
          <Route path="/book" element={<ProtectedLayout><AdminOnly><BookAppointment /></AdminOnly></ProtectedLayout>} />
          <Route path="/dispatch" element={<ProtectedLayout><AdminOnly><Dispatch /></AdminOnly></ProtectedLayout>} />
          <Route path="/forms" element={<ProtectedLayout><AdminOnly><FormsList /></AdminOnly></ProtectedLayout>} />
          <Route path="/users" element={<ProtectedLayout><AdminOnly><Users /></AdminOnly></ProtectedLayout>} />
          <Route path="/data-safety" element={<ProtectedLayout><AdminOnly><DataSafety /></AdminOnly></ProtectedLayout>} />
          <Route path="/training" element={<SharedLayout><Training /></SharedLayout>} />
          <Route path="/training/lesson/:id" element={<SharedLayout><TrainingLesson /></SharedLayout>} />
          <Route path="/training/quiz/:id" element={<SharedLayout><TrainingQuiz /></SharedLayout>} />
          <Route path="/training/test/:key" element={<SharedLayout><TakeTest /></SharedLayout>} />
          <Route path="/presentations" element={<SharedLayout><Presentations key="present" /></SharedLayout>} />
          <Route path="/practice" element={<SharedLayout><Presentations key="practice" withScript /></SharedLayout>} />
          <Route path="/id-cards" element={<ProtectedLayout><AdminOnly><IDCards /></AdminOnly></ProtectedLayout>} />
          <Route path="/training-admin" element={<ProtectedLayout><AdminOnly><TrainingAdmin /></AdminOnly></ProtectedLayout>} />
          <Route path="/quickbooks" element={<ProtectedLayout><AdminOnly><QuickBooks /></AdminOnly></ProtectedLayout>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
